import { useCallback, useMemo } from 'react';
import { scaleLinear } from 'd3-scale';
import { max, min, ticks, tickStep } from 'd3-array';
import first from 'lodash.first';
import last from 'lodash.last';

export { createLinearScale, useLinearScale };

/**
 * This helper create a linear D3.js scale, based on an
 * array of values, an output range, a minimum domain start.
 *
 * @param {array} values array of values
 * @param {array} range output range
 * @param {number} [minDomainStart=0] the minimum domain start
 * @param {boolean} [isPositionScale=false] does this scale process charts positions?
 * @returns {function}
 */
function createLinearScale(
  values,
  range,
  minDomainStart = 0,
  isPositionScale = false
) {
  if (!values) {
    throw new Error('values cannot be undefined.');
  }

  if (range.length !== 2) {
    throw new Error('You should pass a range with a length of two.');
  }

  // Create the domain
  let domain = [
    min(values) < minDomainStart ? min(values) : minDomainStart,
    max(values) === undefined ? 100 : max(values),
  ];

  // Flip the domain for charts position scales
  if (isPositionScale) {
    domain = domain.reverse();
  }

  // Create the scale
  const scale = scaleLinear().domain(domain).range(range).nice();

  /** When the y scale has only a single value as domain. Extend the domain with 50 on both sides. */
  if (scale.domain()[0] === scale.domain()[1]) {
    if (isPositionScale) {
      scale.domain([
        scale.domain()[0] + 50,
        Math.max(scale.domain()[1] - 50, minDomainStart),
      ]);
    } else {
      scale.domain([
        Math.max(scale.domain()[0] - 50, minDomainStart),
        scale.domain()[1] + 50,
      ]);
    }
  }

  /**
   * The contents of this if-statement extend the domain of the y-scale
   * if necessary for a prettier presentation of data.
   */
  if (!isPositionScale) {
    // Generate nicely spaced number n of ticks based on the chart properties
    const isSmallChart = range[1] < 200;
    const n = isSmallChart ? 4 : 6;
    const nTicks = ticks(...scale.domain(), n);

    // Find out the size of the step between two nicely spaced ticks.
    const stepSize = tickStep(...scale.domain(), n);

    /**
     * If the first of the generated ticks is bigger than the maximum value
     * from the domain, we need to extend the domain downwards with another tickStep,
     */
    if (first(nTicks) > first(scale.domain())) {
      const domain = [first(scale.domain()) - stepSize, last(scale.domain())];
      scale.domain(domain);
    }

    /**
     * If the last of the generated ticks is smaller than the maximum value
     * from the domain, we need to extend the domain with another tickStep,
     */
    if (last(nTicks) < last(scale.domain())) {
      const domain = [first(scale.domain()), last(scale.domain()) + stepSize];
      scale.domain(domain);
    }
  }

  /** Make sure to overwrite the niced y scale to always have 'minDomainStart'
   * as lowest value for position charts. */
  if (isPositionScale) {
    scale.domain([scale.domain()[0], minDomainStart]);
  }

  return scale;
}

/**
 * This hook creates a linear scale based on an array of trends.
 *
 * @param {array} trends array of trends
 * @param {string} [dataKey='value']
 * @param {array} range output range
 * @returns
 */
function useLinearScale(trends, dataKey = 'value', range) {
  // If we are visualising chart position trend lines
  // the domain should start at 1, as a chart position
  // can never be 0.
  const isPositionScale = dataKey === 'position';
  const domainMin = isPositionScale ? 1 : 0;

  const values = useMemo(() => {
    const toValue = (d) => d[dataKey];
    return trends.flatMap((trend) => trend.data.map(toValue));
  }, [dataKey, trends]);

  const scale = useCallback(
    createLinearScale(values, range, domainMin, isPositionScale),
    [values, range]
  );

  return scale;
}
